import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chartjs } from 'app/trello/chartjs.imports';
import { GroupedMap2D } from 'app/trello/models/board-summary';
import { ChartableData, LabelledMinAvgMaxValues } from 'app/trello/models/charts';
import { ReadableSecondsPipe } from 'app/trello/pipes/readable-seconds.pipe';
import { Templating } from 'app/trello/templating.imports';
import { StatisticsExtractor } from 'app/trello/utility/statistics-extractor';
import { array } from 'app/trello/utility/utility-functions';
import { Chart, ChartOptions, ChartType, Color, TooltipItem } from 'chart.js';
import { LegendItem } from 'chart.js/dist/types';
import { BaseChartDirective } from 'ng2-charts';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DialogModule } from 'primeng/dialog';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Chartjs,
    Templating,
    ContextMenuModule,
    DialogModule,
  ],
  providers: [ReadableSecondsPipe],
  selector: 'app-min-avg-max-bar-chart-with-table',
  styleUrls: ['./min-avg-max-bar-chart-with-table.component.css'],
  templateUrl: './min-avg-max-bar-chart-with-table.component.html',
})
export class MinAvgMaxBarChartWithTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input({required: true}) chartData!:
    ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>> |
    ChartableData<LabelledMinAvgMaxValues, GroupedMap2D<number>>[];

  @Input() chartLegend: boolean = true;

  @Input() horizontal: boolean = false;

  @Input() chartContainer: HTMLElement | undefined = undefined;

  @Input() tableTitle: string | undefined;
  @Input() tableGroupColumnLabel: string = '';

  @Input() valueFormat: 'number' | 'human-readable-time' | 'custom' = 'number';
  @Input() valueUnit: 'number' | 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days' = 'number';

  @Input() valueFormatter: (value: any) => string = (value: any) => String(value);

  @ViewChild('canvasContainer') private canvasContainer!: ElementRef;
  @ViewChild(BaseChartDirective) private chartDirective!: BaseChartDirective;

  private chartContainerResizeObserver: ResizeObserver | undefined;

  private get chartInstance(): Chart | undefined {
    return this.chartDirective?.chart;
  }

  private initialized: boolean = false;
  private initializedView: boolean = false;

  private readonly offsetColor: string = 'rgba(0,0,0,0)';
  private readonly defaultMinToAvgColor: string = 'green';
  private readonly defaultAvgToMaxColor: string = 'blue';

  protected readonly canvasContextMenuItems = [
    {
      label: 'View Data',
      command: () => {
        this.showDialog();
      }
    }
  ];

  protected readonly chartType: ChartType = 'bar';
  protected chartOptions: ChartOptions = {};
  protected chartOptionsReady: boolean = false;

  protected mappedChartAxisLabels: string[] = [];

  protected mappedChartDatasets: {
    data: number[];
    label: string;
    backgroundColor?: Color | Color[];
    borderColor?: Color | Color[];
    fill?: boolean;
    stack: string;
    // custom fields ...
    dataPointIndex: number;
    stackLabel: string;
    segment: 'offset' | 'min-to-avg' | 'avg-to-max';
    originalMin?: number[];
    originalAvg?: number[];
    originalMax?: number[];
  }[] = [];

  protected mappedChartDatasetsReady: boolean = false;

  protected mappedTableValues: {
    group: string;
    min: number;
    max: number;
    avg: number;
    individual?: {
      [tableValueIndex: number]: {
        group: string;
        min: number;
        max: number;
        avg: number;
      }
    };
  }[] = [];

  protected mappedTableValuesReady: boolean = false;

  protected multipleDataPoints: boolean = false;
  protected tableDataAvailable: boolean = false;

  protected tableDialogOpen: boolean = false;

  protected readonly array = array;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly readableSecondsPipe: ReadableSecondsPipe,
    private readonly statisticsExtractor: StatisticsExtractor,
  ) {
  }

  public ngOnInit(): void {

    this.initialized = true;

    this.prepareData();
  }

  public ngAfterViewInit(): void {

    this.initializedView = true;

    this.keepCanvasSizeSameAsChartContainer();

    this.prepareChartOptions();

    this.changeDetector.detectChanges();

    if (this.chartInstance) {
      this.chartDirective.update();
    }
  }

  public ngOnChanges(
    changes: SimpleChanges,
  ): void {

    if (changes['chartContainer']) {
      this.keepCanvasSizeSameAsChartContainer();
    }

    if (changes['chartData']) {

      this.prepareData();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }

    if (changes['chartLegend']
      || changes['horizontal']
      || changes['valueFormat']
      || changes['valueUnit']) {

      this.prepareChartOptions();

      if (this.chartInstance) {
        this.chartDirective.update();
      }
    }
  }

  public ngOnDestroy(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  protected showDialog(): void {

    this.tableDialogOpen = true;

    this.changeDetector.detectChanges();
  }

  private keepCanvasSizeSameAsChartContainer(): void {

    if (this.chartContainerResizeObserver) {
      this.chartContainerResizeObserver.disconnect();
    }

    if (!this.chartContainer
      || !this.initializedView) {
      return;
    }

    this.chartContainerResizeObserver =
      new ResizeObserver((entries) => {

        for (const entry of entries) {

          if (entry.target === this.chartContainer) {

            let {width, height} = entry.contentRect;

            width = Math.round(width);
            height = Math.round(height);

            if (this.canvasContainer) {

              this.canvasContainer.nativeElement.style.width = `${width}px`;
              this.canvasContainer.nativeElement.style.height = `${height}px`;
            }
          }
        }
      });

    this.chartContainerResizeObserver.observe(this.chartContainer);
  }

  private prepareChartOptions(): void {

    const horizontal = this.horizontal;

    const labelScale = {
      type: 'category',
      display: true,
      stacked: true,
      title: {
        display: false,
        text: '',
      },
      ticks: {
        autoSkip: true,
        maxTicksLimit: 20,
        maxRotation: 0,
        minRotation: 0,
      },
    } as any;

    const valueScale = {
      beginAtZero: true,
      stacked: true,
      title: {
        display: false,
        text: '',
      },
      ticks: {
        precision: 0,
        callback: (value: string | number) => this.formatValue(Number(value)),
      },
    } as any;

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: horizontal ? 'y' : 'x',
      interaction: {
        axis: horizontal ? 'y' : 'x',
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: horizontal ? valueScale : labelScale,
        y: horizontal ? labelScale : valueScale,
      },
      plugins: {
        legend: {
          display: this.chartLegend,
          labels: {
            generateLabels: (
              chart: Chart<'bar'>,
            ) => {

              const datasets = chart.data.datasets as any[];

              const datasetIndexesByDataPointIndex = new Map<number, number[]>();

              datasets.forEach((dataset: any, datasetIndex: number) => {

                const dataPointIndex: number = dataset.dataPointIndex;

                if (!datasetIndexesByDataPointIndex.has(dataPointIndex)) {
                  datasetIndexesByDataPointIndex.set(dataPointIndex, []);
                }

                datasetIndexesByDataPointIndex.get(dataPointIndex)!.push(datasetIndex);
              });

              const defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);

              const defaultLabelsByDatasetIndex = new Map(
                defaultLabels.map((defaultLabel) => [defaultLabel.datasetIndex!, defaultLabel])
              );

              const labels: LegendItem[] = [];

              for (const [dataPointIndex, stackDatasetIndices] of datasetIndexesByDataPointIndex.entries()) {

                const referenceIndexFromStack = stackDatasetIndices[1] ?? stackDatasetIndices[0];
                const referenceDataset = datasets[referenceIndexFromStack];

                const defaultLabel = defaultLabelsByDatasetIndex.get(referenceIndexFromStack) || {};

                const labelText = ((referenceDataset as any).stackLabel) || referenceDataset.label || `Dataset #${dataPointIndex}`;

                const stackVisible = stackDatasetIndices.some((index) => chart.isDatasetVisible(index));

                labels.push({
                  ...defaultLabel,
                  hidden: !stackVisible,
                  text: labelText,
                  // custom fields ...
                  stackDatasetIndices,
                } as any);
              }

              return labels;
            },
          },
          onClick: (
            _,
            legendItem,
            legend,
          ) => {

            const chart = legend.chart;

            const stackDatasetIndices = (((legendItem as any).stackDatasetIndices) || []) as [];

            const stackVisible = stackDatasetIndices.every((index) => chart.isDatasetVisible(index));

            stackDatasetIndices.forEach((index) => {

              if (stackVisible) {
                chart.hide(index);
              } else {
                chart.show(index);
              }
            });
          },
        },
        tooltip: {
          position: 'nearest',
          callbacks: {
            label: (context: TooltipItem<any>) => {

              const dataset = context.dataset as any;

              if (dataset?.segment !== 'min-to-avg') {
                return '';
              }

              const i = context.dataIndex;

              const min = this.formatValue(dataset.originalMin?.[i] ?? 0);
              const avg = this.formatValue(dataset.originalAvg?.[i] ?? 0);
              const max = this.formatValue(dataset.originalMax?.[i] ?? 0);

              const stackLabel = dataset.stackLabel || 'Dataset';

              return `${stackLabel}: min=${min}, avg=${avg}, max=${max}`;
            },
          },
        },
      },
    };

    (this.chartOptions.plugins as any).minAvgMaxAverageIndicator = {
      enabled: true,
    };

    (this.chartOptions.plugins as any).minAvgMaxDrawZeros = {
      enabled: true,
    };

    (this.chartOptions.plugins as any).timeBasedTicks = {
      valueAxis: horizontal ? 'x' : 'y',
      valueUnit: this.valueUnit,
    };

    this.chartOptionsReady = true;
  }

  private prepareData(): void {

    this.prepareChartDatasets();
    this.prepareTableValues();

    this.multipleDataPoints =
      Array.isArray(this.chartData)
      && this.chartData.length > 1;
  }

  private prepareChartDatasets(): void {

    if (!this.initialized) {
      return;
    }

    const chartDataPoints =
      Array.isArray(this.chartData)
        ? this.chartData
        : [this.chartData as ChartableData<LabelledMinAvgMaxValues, unknown>];

    if (chartDataPoints.length === 0) {

      this.mappedChartAxisLabels = [];

      this.mappedChartDatasets = [];
      this.mappedChartDatasetsReady = true;

      return;
    }

    const firstDatasetLabels = chartDataPoints[0].chartData.labels || [];

    const allLabelsSame =
      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels || [])
        .every((labels: string[]) => {

          if (labels.length !== firstDatasetLabels.length) {
            return false;
          }

          return labels.every((label, index) => label === firstDatasetLabels[index]);
        });

    if (allLabelsSame) {

      this.mappedChartAxisLabels = [...firstDatasetLabels];

    } else {

      const uniqueLabels = new Set<string>();

      chartDataPoints
        .map((chartDataPoint) => chartDataPoint.chartData)
        .map((labelledValues) => labelledValues.labels || [])
        .forEach((labels: string[]) => {

          [...labels].forEach((label: string) => uniqueLabels.add(label));
        });

      this.mappedChartAxisLabels = Array.from(uniqueLabels).sort();
    }

    this.mappedChartDatasets =
      chartDataPoints
        .flatMap((chartDataPoint: ChartableData<LabelledMinAvgMaxValues, unknown>, dataPointIndex: number) => {

          const stackId = `dataPoint-${dataPointIndex}`;

          const {labels, min, avg, max} = chartDataPoint.chartData;

          const alignedMin = this.alignValues(min, labels, this.mappedChartAxisLabels);
          const alignedAvg = this.alignValues(avg, labels, this.mappedChartAxisLabels);
          const alignedMax = this.alignValues(max, labels, this.mappedChartAxisLabels);

          const offsets: number[] = [];
          const minToAvg: number[] = [];
          const avgToMax: number[] = [];

          for (let i = 0; i < this.mappedChartAxisLabels.length; i++) {

            offsets.push(alignedMin[i]);

            minToAvg.push(alignedAvg[i] - alignedMin[i]);
            avgToMax.push(alignedMax[i] - alignedAvg[i]);
          }

          return [
            {
              data: offsets,
              label: '',
              backgroundColor: this.offsetColor,
              borderColor: this.offsetColor,
              fill: false,
              stack: stackId,
              // custom fields ...
              dataPointIndex,
              stackLabel: chartDataPoint.chartLabel ?? '',
              segment: 'offset',
              originalMin: alignedMin,
              originalAvg: alignedAvg,
              originalMax: alignedMax,
            },
            {
              data: minToAvg,
              label: chartDataPoint.chartLabel ? chartDataPoint.chartLabel + ' (Min→Avg)' : 'Min→Avg',
              backgroundColor: chartDataPoint.primaryColor ?? this.defaultMinToAvgColor,
              borderColor: chartDataPoint.primaryColor ?? this.defaultMinToAvgColor,
              fill: chartDataPoint.fill ?? false,
              stack: stackId,
              // custom fields ...
              dataPointIndex,
              stackLabel: chartDataPoint.chartLabel ?? '',
              segment: 'min-to-avg',
              originalMin: alignedMin,
              originalAvg: alignedAvg,
              originalMax: alignedMax,
            },
            {
              data: avgToMax,
              label: chartDataPoint.chartLabel ? chartDataPoint.chartLabel + ' (Avg→Max)' : 'Avg→Max',
              backgroundColor: chartDataPoint.primaryColor ?? this.defaultAvgToMaxColor,
              borderColor: chartDataPoint.primaryColor ?? this.defaultAvgToMaxColor,
              fill: chartDataPoint.fill ?? false,
              stack: stackId,
              // custom fields ...
              dataPointIndex,
              stackLabel: chartDataPoint.chartLabel ?? '',
              segment: 'avg-to-max',
              originalMin: alignedMin,
              originalAvg: alignedAvg,
              originalMax: alignedMax,
            },
          ];
        });

    this.mappedChartDatasetsReady = true;
  }

  private prepareTableValues(): void {

    if (!this.initialized) {
      return;
    }

    const chartDataPoints =
      Array.isArray(this.chartData)
        ? this.chartData
        : [this.chartData];

    if (chartDataPoints.length === 0) {

      this.mappedTableValues = [];
      this.mappedTableValuesReady = true;

      this.tableDataAvailable = false;

      return;
    }

    const allStatsPerDataset =
      chartDataPoints.map((chartDataPoint) => {
        return this.statisticsExtractor.aggregateNumericStatsByGroup(chartDataPoint.tableData);
      });

    const allStatsPerDatasetLookup =
      allStatsPerDataset.map((statsPerDataset) =>
        new Map(
          statsPerDataset.map((stats) => [stats.group, stats]),
        ),
      );

    const allGroups =
      new Set<string>(
        allStatsPerDataset.flatMap((statsPerDataset) =>
          statsPerDataset.map((stats) => stats.group),
        ),
      );

    this.mappedTableValues =
      Array.from(allGroups).sort()
        .map((group) => {

          let aggregatedMin = Number.POSITIVE_INFINITY;
          let aggregatedMax = Number.NEGATIVE_INFINITY;
          let aggregatedAvgSum = 0;
          let aggregatedCount = 0;

          const individual: {
            [datasetIndex: number]: {
              group: string;
              min: number;
              max: number;
              avg: number;
            }
          } = {};

          allStatsPerDatasetLookup.forEach((statsPerDatasetByGroup, i) => {

            const stats = statsPerDatasetByGroup.get(group);

            if (stats) {

              if (stats.min < aggregatedMin) {
                aggregatedMin = stats.min;
              }

              if (stats.max > aggregatedMax) {
                aggregatedMax = stats.max;
              }

              aggregatedAvgSum += stats.avg;
              aggregatedCount++;

              individual[i] = {
                group,
                min: stats.min,
                avg: stats.avg,
                max: stats.max,
              };

            } else {

              individual[i] = {
                group,
                min: 0,
                avg: 0,
                max: 0,
              };
            }
          });

          if (aggregatedMin === Number.POSITIVE_INFINITY) {
            aggregatedMin = 0;
          }

          if (aggregatedMax === Number.NEGATIVE_INFINITY) {
            aggregatedMax = 0;
          }

          const aggregatedAvg = aggregatedCount > 0 ? Math.round(aggregatedAvgSum / aggregatedCount) : 0;

          return {
            group,
            min: aggregatedMin,
            avg: aggregatedAvg,
            max: aggregatedMax,
            individual,
          };
        });

    this.mappedTableValuesReady = true;

    this.tableDataAvailable = this.mappedTableValues.length > 0;
  }

  private alignValues(
    values: number[],
    labels: string[],
    allLabels: string[],
  ): number[] {

    if (!labels || values.length !== labels.length) {
      return new Array(allLabels.length).fill(0);
    }

    const allLabelsSame =
      allLabels.length === labels.length &&
      allLabels.every((label: string, i: number) => label === labels[i]);

    if (allLabelsSame) {
      return [...values];
    }

    const valueByLabel = new Map<string, number>();

    labels.forEach((label: string, i: number) => {
      valueByLabel.set(label, values[i]);
    });

    return allLabels.map((label: string): number => valueByLabel.get(label) ?? 0);
  }

  private formatValue(
    value: any,
  ): string {

    if (this.valueFormat === 'custom') {
      return this.valueFormatter(value);
    }

    if (this.valueFormat === 'human-readable-time') {

      const seconds = Number(value) / 1000;

      return this.readableSecondsPipe.transform(seconds, 1) || '';
    }

    return String(value);
  }

}
